import React, { useEffect, useRef, useState } from 'react';

import { Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Spinner } from '@lightspeed/flame/Spinner';
import { Heading3, Text } from '@lightspeed/flame/Text';

import { PAYMENT_STATUS, TERMINAL_EVENT } from '@lightspeed-payments/payments-terminal-sdk';
import Decimal from 'app/src/data/base/Decimal';
import CommonData from 'app/src/data/CommonData';
import Format from 'app/src/data/Format';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import SaleManager from 'app/src/payments/SaleManager';
import { POLLING_EVENT } from 'app/src/payments/terminals/PaymentServiceTerminal';
import Terminal from 'app/src/payments/terminals/Terminal';
import { doCreditQuickPay } from 'payments/QuickPay';
import PaymentServiceAlert from 'root/apps/app/src/controllers/payments/PaymentServiceAlert';
import { validateMinAmount } from 'root/apps/app/src/payments/amount-validation';
import { trackAnalyticsEvent } from 'root/apps/helpers/AnalyticsTracking';

import { TransactionState } from './commonData';
import {
  ModalBodyContent,
  ModalExtraBodyContent,
  ModalFooterContent,
} from './QuickPayPaymentModalContent';

// Types
interface QuickPayPaymentModalProps {
  onHide: () => void;
  onPaymentComplete: (result: PaymentResponse) => Promise<void>;
  initialAmount?: Decimal;
  paymentProvider: PaymentProvider;
  saleManager: SaleManager;
  terminal?: any;
  tipAmount?: number;
}

const QuickPayPaymentModal: React.FC<QuickPayPaymentModalProps> = ({
  onHide,
  onPaymentComplete,
  initialAmount = 0,
  paymentProvider,
  saleManager,
  terminal,
  tipAmount = 0,
}) => {
  const timeStart = Date.now();
  const terminalCollection = paymentProvider.terminalCollection;
  const terminalSelectionFromCommonData = CommonData.selectedRegister.get('ccTerminalID');

  // First get the last used Terminal's terminalID from the common data - sometimes it can be 'select-terminal'
  // If it's not 'select-terminal' then use it, otherwise use the first terminal in the collection
  // There's a check before this to make sure that there's at least one terminal in the collection
  const terminalID =
    terminalSelectionFromCommonData !== 'select-terminal' && terminalSelectionFromCommonData;
  const selectedTerminal =
    terminal ||
    paymentProvider.getTerminal(terminalID) ||
    paymentProvider.getTerminal(terminalCollection.models.first()?.id);

  const supportedFeatures = PaymentProvider.supportedFeaturesForGateway(
    paymentProvider.activeGateway?.attributes || {}
  );

  /**
   * Handles the complexity of disabling the cancel button
   * Cancel is disabled if the gateways supports Cancel and if it's not an unreferencedRefund
   * OR if the gateway is lightspeed payments and supports cancel
   */
  const apiCanCancel = Boolean(
    supportedFeatures?.supportApiCancel && paymentProvider.isGatewayLightspeedPayments
  );

  const [payment, setPayment] = useState<any>(null);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [transactionState, setTransactionState] = useState<string>(
    TransactionState.WAITING_TO_START
  );
  const [syncingWithServer, setSyncingWithServer] = useState<boolean>(false);
  const [transactionMessage, setTransactionMessage] = useState<string | null>(null);
  const [isCancelDisabled, setIsCancelDisabled] = useState<boolean>(apiCanCancel);
  const [showRetryButton, setShowRetryButton] = useState<boolean>(true);
  const canCompletePayment = useRef<boolean>(true);

  const handleTerminalSelectionOption = () => {
    // Cleanup terminal Operation Before changing terminal
    canCompletePayment.current = false;
    handleCancelWhileInProgress();
    doCreditQuickPay({
      saleManager,
      paymentProvider,
      terminalSelectionOption: true,
    });
  };

  const handleCloseModal = () => {
    trackAnalyticsEvent({
      eventCategory: 'QuickPay',
      eventName: 'QuickPay Clicked',
      eventAction: 'Modal Closed',
      eventValue: `${Math.abs((Date.now() - timeStart) / 1000).toString()}seconds`,
      eventLabel: 'QuickPay-V2',
    });
    setIsOpen(false);
    onHide();
  };

  const handleCancelWhileInProgress = () => {
    setSyncingWithServer(true);
    if (apiCanCancel) {
      PaymentOperations.cancelPayment({ terminal: selectedTerminal, payment }).catch(
        (response: { response: string }) => {
          switch (response.response) {
            case Terminal.Response.NON_CANCELLABLE_STATE:
              setTransactionState(TransactionState.NON_CANCELLABLE_STATE);
              setIsCancelDisabled(false);
              break;
            case Terminal.Response.CANCEL_UNAVAILABLE:
              setTransactionState(TransactionState.CANCEL_UNAVAILABLE);
              setIsCancelDisabled(false);
              break;
            default:
              setTransactionState(TransactionState.SERVICE_EXCEPTION);
          }
        }
      );
    } else {
      setTransactionState(TransactionState.CANCEL_FROM_TERMINAL);
    }
  };

  const handlePollingStatusUpdate = (event: Event) => {
    const customEvent = event as CustomEvent<string>;
    switch (customEvent.detail) {
      case POLLING_EVENT.STATUS.DEGRADED:
        setTransactionState(TransactionState.CONNECTION_DEGRADED);
        break;
      case POLLING_EVENT.STATUS.KO:
        setTransactionState(TransactionState.CONNECTION_KO);
        break;
      case POLLING_EVENT.STATUS.OK:
        setTransactionState(TransactionState.IN_PROGRESS);
        break;
      default:
        throw new Error(`Unhandled polling state ${customEvent.detail}`);
    }
  };

  const handleTransactionEvents = (event: Event) => {
    const customEvent = event as CustomEvent<any>;
    const { payment: detailPayment, disableCancelButton } = customEvent.detail;
    if (apiCanCancel) {
      if (detailPayment) {
        setIsCancelDisabled(false);
        setPayment(detailPayment);
      }
      if (typeof disableCancelButton === 'boolean') {
        setIsCancelDisabled(disableCancelButton);
      }
    }
  };

  const allowCloseWithEsc =
    transactionState !== TransactionState.IN_PROGRESS &&
    transactionState !== TransactionState.COMPLETE &&
    transactionState !== TransactionState.CANCEL_FROM_POS_UNREFERENCED_REFUND;

  // On mount, start the payment process
  useEffect(
    () => {
      // Polling for terminal connection
      window.addEventListener(POLLING_EVENT.NAME, handlePollingStatusUpdate);
      window.addEventListener(Terminal.TerminalEvents.PAYMENT_STARTED, handleTransactionEvents);
      window.addEventListener(Terminal.TerminalEvents.CANCEL_STARTED, handleTransactionEvents);

      initiateQuickPay({ isRetry: false });
      // eslint-disable-next-line consistent-return
      return () => {
        // Cleanup
        window.removeEventListener(POLLING_EVENT.NAME, handlePollingStatusUpdate);
        window.removeEventListener(
          Terminal.TerminalEvents.PAYMENT_STARTED,
          handleTransactionEvents
        );
        window.removeEventListener(Terminal.TerminalEvents.CANCEL_STARTED, handleTransactionEvents);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const initiateQuickPay = async ({ isRetry = false }: { isRetry?: boolean }) => {
    trackAnalyticsEvent({
      eventCategory: 'QuickPay',
      eventName: 'QuickPay Clicked',
      eventAction: 'Sale Started',
      eventLabel: 'QuickPay-V2',
    });
    // 1. Validate the amount
    try {
      validateMinAmount(
        saleManager.pendingBalance,
        window.merchantos?.locale?.currency_code,
        paymentProvider.isGatewayLightspeedPayments
      );
    } catch (error) {
      handleCloseModal();
      PaymentServiceAlert.alert(error.message);
      return;
    }

    // 2. Check if the terminal should start LSPAY monitoring
    if (paymentProvider.isGatewayLightspeedPayments && selectedTerminal?.hasMonitoringModule) {
      selectedTerminal.startLspayMonitoring(paymentProvider.activeGatewayPli, {
        isRetry,
        isQuickPay: true,
      });
    }

    // 3. Start the transaction
    setTransactionState(TransactionState.IN_PROGRESS);
    setTransactionMessage(null);
    setIsCancelDisabled(apiCanCancel);
    setPayment(null);

    // 4. Start the payment operation
    PaymentOperations.doTerminalPaymentOperation({
      amount: new Decimal(initialAmount),
      terminal: selectedTerminal,
      saleManager,
      gatewayConfig: paymentProvider.activeGateway,
      ...(tipAmount && { tipAmount }),
    })
      .done((response) => {
        switch (response.response) {
          case Terminal.Response.APPROVED:
          case Terminal.Response.PAYMENT_ALREADY_RECONCILED:
            setPayment(response.payment);
            setTransactionState(TransactionState.COMPLETE);
            trackAnalyticsEvent({
              eventCategory: 'QuickPay',
              eventName: 'QuickPay Clicked',
              eventAction: 'Payment Completed successfully',
              eventValue: `${Math.abs((Date.now() - timeStart) / 1000).toString()}seconds`,
              eventLabel: 'QuickPay-V2',
            });
            break;

          default:
            setTransactionState(TransactionState.ERROR);
            setTransactionMessage('An unexpected error has occurred');
            break;
        }
      })
      .fail((response) => {
        trackAnalyticsEvent({
          eventCategory: 'QuickPay',
          eventName: 'QuickPay Clicked',
          eventAction: 'Payment Failed',
          eventValue: `${Math.abs((Date.now() - timeStart) / 1000).toString()}seconds`,
          eventLabel: 'QuickPay-V2',
        });

        const message =
          response?.payment?.serviceModel?.paymentTransaction?.parameters?.message ||
          response.error;
        const detailedErrorMessage =
          response?.payment?.serviceModel?.paymentTransaction?.parameters?.detailedMessage || null;
        const compoundMessage = detailedErrorMessage
          ? `${message} - ${detailedErrorMessage}`
          : message;
        setTransactionMessage(compoundMessage);
        setPayment(response.payment);

        switch (response.response) {
          case Terminal.Response.CANCELLED_FROM_TERMINAL:
          case Terminal.Response.CANCELLED:
            setTransactionState(TransactionState.CANCELED);
            break;
          case Terminal.Response.DECLINED:
            setTransactionState(TransactionState.DECLINED);
            break;
          case Terminal.Response.CANCELLED_FROM_POS:
            setTransactionState(TransactionState.CANCELLED_FROM_POS);
            break;
          case Terminal.Response.ERROR:
          case Terminal.Response.TERMINAL_ERROR:
          case Terminal.Response.TIMEOUT:
            setTransactionState(TransactionState.ERROR);
            if (
              transactionMessage &&
              transactionMessage.includes(
                'The selected terminal is no longer paired to this account'
              )
            ) {
              setShowRetryButton(false);
            }
            break;
          case Terminal.Response.SERVICE_EXCEPTION:
            setTransactionState(TransactionState.SERVICE_EXCEPTION);
            break;
          default:
            setTransactionState(TransactionState.ERROR);
            setTransactionMessage('An unknown error has occurred.');
            break;
        }
      })
      .always((response) => {
        const { payment, terminalMonitoringStatus, responseCode } = response;
        const reasonPrefix = responseCode ?? response.code ?? '';
        const reasonSuffix =
          response?.payment?.serviceModel?.paymentTransaction?.attributes?.message ||
          response.error ||
          response.message ||
          '';

        selectedTerminal?.lspayMonitoring &&
          selectedTerminal.lspayMonitoring.sendEvent(TERMINAL_EVENT.COMPLETE, {
            payment: {
              id: payment?.serviceModel?.id,
              amount: payment?.amount?.toNumber(),
              currency: window.merchantos?.locale?.currency_code,
              status: !payment ? PAYMENT_STATUS.ERROR : terminalMonitoringStatus.toLowerCase(), // Using toLowerCase() to Mmatch PAYMENT_STATUS type with a safe approach
              reason:
                `${
                  reasonPrefix && reasonSuffix
                    ? `${reasonPrefix} - ${reasonSuffix}`
                    : reasonPrefix || reasonSuffix
                }` || undefined,
              ...(response instanceof Error && {
                error: response,
              }),
            },
            terminalTransactionResponse: response,
          });

        if (
          response.response === Terminal.Response.APPROVED ||
          response.response === Terminal.Response.PAYMENT_ALREADY_RECONCILED
        ) {
          completePayment();
        }
      });
  };

  const completePayment = () => {
    if (!canCompletePayment.current) {
      return;
    }
    setSyncingWithServer(true);
    // Set the payment as complete if the terminalSelectionOption is not selected
    onPaymentComplete(payment)
      .then(() => {
        setSyncingWithServer(false);
      })
      .catch(() => {
        setSyncingWithServer(false);
      })
      .finally(() => {
        handleCloseModal();
      });
  };

  const handleRetryPaymentClicked = () => {
    PaymentOperations.abandonTerminalPaymentOperation({ terminal: selectedTerminal });

    setShowRetryButton(true);
    setIsCancelDisabled(false);
    setSyncingWithServer(false);
    // trigger payment cylce again
    initiateQuickPay({ isRetry: true });
  };

  const handleBackInProgress = () => {
    setTransactionState(TransactionState.IN_PROGRESS);
  };

  const title = selectedTerminal?.model?.get('name') || 'Process card payment';

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      closeOnEsc={allowCloseWithEsc}
      shouldCloseOnOverlayClick={false}
    >
      <ModalHeader showCloseButton={false}>
        <div className="quick-pay-modal__header">{title}</div>
      </ModalHeader>
      <ModalBody>
        <Flex flexDirection="column" alignItems="center" justifyContent="space-between" mt={2}>
          {transactionState === TransactionState.IN_PROGRESS ? (
            <>
              <Spinner color="blue-300" baseColor2="gray-200" size="96px" />
              <Heading3 mt="4" mb="2">
                Complete payment on terminal
              </Heading3>
              <Text color="gray-600" size="small">
                Ask the client to follow the instructions on the terminal
              </Text>
              <Divider my={4} />
              <Heading3 data-test="payment-amount">{Format.formatMoney(initialAmount, 0)}</Heading3>
            </>
          ) : (
            <ModalBodyContent
              transactionState={transactionState}
              transactionMessage={transactionMessage}
              selectedTerminal={selectedTerminal}
            />
          )}
          <ModalExtraBodyContent
            salePaymentId={payment?.model?.id?.toString() ?? undefined}
            transactionState={transactionState}
            paymentProvider={paymentProvider}
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <ModalFooterContent
          transactionState={transactionState}
          showRetryButton={showRetryButton}
          isCancelDisabled={isCancelDisabled}
          handleCloseModal={handleCloseModal}
          handleBackInProgress={handleBackInProgress}
          handleRetryPaymentClicked={handleRetryPaymentClicked}
          handleCancelWhileInProgress={handleCancelWhileInProgress}
          syncingWithServer={syncingWithServer}
          handleTerminalSelectionOption={handleTerminalSelectionOption}
          multiTerminal={terminalCollection.length > 1}
        />
      </ModalFooter>
    </Modal>
  );
};

export { QuickPayPaymentModal };
